import { PERMISSIONS } from '@jupi/shared'

// The member app's navigation, in one place.
//
// The intranet is split into AREAS -- Bookings, Fleet, Flight School,
// Club -- each with its own handful of pages. The shell (components/
// AppShell.tsx) shows the areas as the top level (AppBar tabs on a desktop,
// the bottom bar on a phone) and only the CURRENT area's pages beneath
// them (the side drawer on a desktop, a tab strip on a phone). Nobody has
// to scan fifteen links to find the three they use.
//
// An item shows if the member holds ANY of its permissions (none listed =
// every signed-in member), and an area shows if any of its items does --
// so someone with no flight school role never sees Flight School at all.
// Keep each list in step with the API's requirePermission for the page's
// data, or a visible link leads to a 403.

export interface AreaItem {
  to: string
  labelKey: string
  icon: string
  permissions?: string[]
  // The area's primary action (e.g. "New booking"): a floating button on a
  // phone rather than one more tab, a normal entry in the desktop drawer.
  fab?: boolean
}

export interface Area {
  id: string
  labelKey: string
  icon: string
  basePath: string
  items: AreaItem[]
}

const SCHOOL = {
  atoStudent: PERMISSIONS.fsmAtoStudent,
  dtoStudent: PERMISSIONS.fsmDtoStudent,
  atoInstruct: PERMISSIONS.fsmAtoInstruct,
  dtoInstruct: PERMISSIONS.fsmDtoInstruct,
  atoManage: PERMISSIONS.fsmAtoManage,
  dtoManage: PERMISSIONS.fsmDtoManage,
}
const ANY_SCHOOL_ROLE = Object.values(SCHOOL)

export const AREAS: Area[] = [
  {
    id: 'bookings',
    labelKey: 'nav.areaBookings',
    icon: 'mdi:calendar-clock',
    basePath: '/bookings',
    items: [
      { to: '/bookings', labelKey: 'nav.today', icon: 'mdi:calendar-today', permissions: [PERMISSIONS.bookingRead] },
      {
        to: '/bookings/calendar',
        labelKey: 'nav.calendar',
        icon: 'mdi:calendar-month',
        permissions: [PERMISSIONS.bookingRead],
      },
      {
        to: '/bookings/mine',
        labelKey: 'nav.myBookings',
        icon: 'mdi:clipboard-list-outline',
        permissions: [PERMISSIONS.bookingRead],
      },
      // The quick way to a lesson, for those in the flight school.
      {
        to: '/bookings/wizard',
        labelKey: 'nav.bookLesson',
        icon: 'mdi:calendar-search',
        permissions: [SCHOOL.atoStudent, SCHOOL.dtoStudent, SCHOOL.atoInstruct, SCHOOL.dtoInstruct],
      },
      {
        to: '/bookings/new',
        labelKey: 'nav.newBooking',
        icon: 'mdi:plus',
        permissions: [PERMISSIONS.bookingCreate],
        fab: true,
      },
    ],
  },
  {
    id: 'fleet',
    labelKey: 'nav.areaFleet',
    icon: 'mdi:airplane',
    basePath: '/fleet',
    items: [
      { to: '/fleet', labelKey: 'nav.aircraft', icon: 'mdi:airplane', permissions: [PERMISSIONS.aircraftRead] },
      // The flight log, and logging a flight as the area's floating button:
      // the one thing a pilot does in Fleet after every flight.
      {
        to: '/fleet/logs',
        labelKey: 'nav.flightLog',
        icon: 'mdi:notebook-outline',
        permissions: [PERMISSIONS.flightLogRead],
      },
      {
        to: '/fleet/logbook',
        labelKey: 'nav.journeyLogBook',
        icon: 'mdi:book-open-page-variant-outline',
        permissions: [PERMISSIONS.flightLogRead],
      },
      {
        to: '/fleet/logs/new',
        labelKey: 'nav.logFlight',
        icon: 'mdi:notebook-plus-outline',
        permissions: [PERMISSIONS.flightLogCreate, PERMISSIONS.flightLogManage],
        fab: true,
      },
      {
        to: '/fleet/maintenance',
        labelKey: 'nav.maintenance',
        icon: 'mdi:wrench-outline',
        permissions: [PERMISSIONS.maintenanceRead],
      },
      {
        to: '/fleet/messages',
        labelKey: 'nav.messages',
        icon: 'mdi:message-alert-outline',
        permissions: [PERMISSIONS.maintenanceRead],
      },
      {
        to: '/fleet/defects',
        labelKey: 'nav.defects',
        icon: 'mdi:alert-circle-outline',
        permissions: [PERMISSIONS.defectReport, PERMISSIONS.defectTriage],
      },
    ],
  },
  // The flight planner, folded in from webplan-modern. Built for a phone at
  // the airfield as much as a desk at home. Every page needs fpl.plan.use,
  // like the /api/plan router.
  {
    id: 'plan',
    labelKey: 'nav.areaPlan',
    icon: 'mdi:map-marker-path',
    basePath: '/plan',
    items: [
      { to: '/plan', labelKey: 'nav.plan', icon: 'mdi:map-marker-path', permissions: [PERMISSIONS.fplPlanUse] },
      { to: '/plan/map', labelKey: 'nav.planMap', icon: 'mdi:map-outline', permissions: [PERMISSIONS.fplPlanUse] },
      {
        to: '/plan/weather',
        labelKey: 'nav.planWeather',
        icon: 'mdi:weather-partly-cloudy',
        permissions: [PERMISSIONS.fplPlanUse],
      },
      { to: '/plan/atis', labelKey: 'nav.planAtis', icon: 'mdi:radio-tower', permissions: [PERMISSIONS.fplPlanUse] },
      {
        to: '/plan/notams',
        labelKey: 'nav.planNotams',
        icon: 'mdi:alert-octagon-outline',
        permissions: [PERMISSIONS.fplPlanUse],
      },
      {
        to: '/plan/weight',
        labelKey: 'nav.planWeight',
        icon: 'mdi:scale-balance',
        permissions: [PERMISSIONS.fplPlanUse],
      },
      {
        to: '/plan/gps',
        labelKey: 'nav.planGps',
        icon: 'mdi:satellite-variant',
        permissions: [PERMISSIONS.fplPlanUse],
      },
      {
        to: '/plan/fuel',
        labelKey: 'nav.planFuel',
        icon: 'mdi:gas-station',
        permissions: [PERMISSIONS.fplPlanUse],
      },
      {
        to: '/plan/crosswind',
        labelKey: 'nav.planCrosswind',
        icon: 'mdi:windsock',
        permissions: [PERMISSIONS.fplPlanUse],
      },
      { to: '/plan/routes', labelKey: 'nav.planRoutes', icon: 'mdi:routes', permissions: [PERMISSIONS.fplPlanUse] },
      {
        to: '/plan/waypoints',
        labelKey: 'nav.planWaypoints',
        icon: 'mdi:map-marker-multiple-outline',
        permissions: [PERMISSIONS.fplPlanUse],
      },
      { to: '/plan/ofp', labelKey: 'nav.planOfp', icon: 'mdi:clipboard-text-outline', permissions: [PERMISSIONS.fplPlanUse] },
      { to: '/plan/links', labelKey: 'nav.planLinks', icon: 'mdi:link-variant', permissions: [PERMISSIONS.fplPlanUse] },
    ],
  },
  // Only for people in the flight school -- a student, instructor or head
  // of training in either department. A member with none of those never
  // sees the area. Pages arrive as the school is ported.
  {
    id: 'school',
    labelKey: 'nav.areaSchool',
    icon: 'mdi:school-outline',
    basePath: '/school',
    items: [
      {
        to: '/school/training',
        labelKey: 'nav.myTraining',
        icon: 'mdi:school-outline',
        permissions: [SCHOOL.atoStudent, SCHOOL.dtoStudent],
      },
      {
        to: '/school/students',
        labelKey: 'nav.myStudents',
        icon: 'mdi:account-school-outline',
        permissions: [SCHOOL.atoInstruct, SCHOOL.dtoInstruct],
      },
      {
        to: '/school/lessons',
        labelKey: 'nav.myLessons',
        icon: 'mdi:calendar-account-outline',
        permissions: [SCHOOL.atoInstruct, SCHOOL.dtoInstruct],
      },
      // ATO only: DTO training has no duty-time regulation.
      {
        to: '/school/duty',
        labelKey: 'nav.dutyLog',
        icon: 'mdi:clock-time-eight-outline',
        permissions: [SCHOOL.atoInstruct, SCHOOL.atoStudent],
      },
      // Instructors and heads of training see their departments' courses;
      // a student sees the ones they are enrolled on.
      {
        to: '/school/courses',
        labelKey: 'nav.courses',
        icon: 'mdi:book-open-page-variant-outline',
        permissions: ANY_SCHOOL_ROLE,
      },
      {
        to: '/school/instructors',
        labelKey: 'nav.instructors',
        icon: 'mdi:account-tie-outline',
        permissions: ANY_SCHOOL_ROLE,
      },
      {
        to: '/school/briefings',
        labelKey: 'nav.briefings',
        icon: 'mdi:book-open-page-variant',
        permissions: ANY_SCHOOL_ROLE,
      },
      { to: '/school/manuals', labelKey: 'nav.manuals', icon: 'mdi:bookshelf', permissions: ANY_SCHOOL_ROLE },
    ],
  },
  // The palolento (fire-patrol) calendar and its crew list. Only for the
  // fire patrol roles, the board and administrators (flight.firepatrol.*);
  // an observer from outside the club sees nothing else.
  {
    id: 'firepatrol',
    labelKey: 'nav.areaFirePatrol',
    icon: 'mdi:fire',
    basePath: '/fire-patrol',
    items: [
      {
        to: '/fire-patrol',
        labelKey: 'nav.firePatrolCalendar',
        icon: 'mdi:calendar-month',
        permissions: [PERMISSIONS.firePatrolRead],
      },
      {
        to: '/fire-patrol/crew',
        labelKey: 'nav.firePatrolCrew',
        icon: 'mdi:account-group-outline',
        permissions: [PERMISSIONS.firePatrolDispatch],
      },
    ],
  },
  {
    id: 'club',
    labelKey: 'nav.areaClub',
    icon: 'mdi:account-group-outline',
    basePath: '/club',
    items: [
      // Every member (not an observer): public and members-only events,
      // plus the personal calendar-subscription link.
      { to: '/club/events', labelKey: 'nav.events', icon: 'mdi:calendar-star', permissions: [PERMISSIONS.eventRead] },
      {
        to: '/club/members',
        labelKey: 'nav.members',
        icon: 'mdi:account-group-outline',
        permissions: [PERMISSIONS.memberRead, PERMISSIONS.memberDirectoryRead],
      },
      // Club-wide charts (per aircraft, a few hours old) and the member's
      // own statistics. Same permission as the flight log it is drawn from
      // (/api/stats).
      {
        to: '/club/stats',
        labelKey: 'nav.stats',
        icon: 'mdi:chart-bar',
        permissions: [PERMISSIONS.flightLogRead],
      },
      // Rules, forms, bulletins and meeting papers. The board's own papers
      // show only to those with the board permission (the API decides).
      {
        to: '/club/documents',
        labelKey: 'nav.clubDocuments',
        icon: 'mdi:file-document-multiple-outline',
        permissions: [PERMISSIONS.documentRead, PERMISSIONS.documentReadBoard],
      },
      // The club's knowledge tests and onboarding checks (the SMS test, the
      // club orientation check). Sitting one needs intra.exam.take.
      { to: '/club/exams', labelKey: 'nav.clubExams', icon: 'mdi:clipboard-check-outline', permissions: [PERMISSIONS.examTake] },
      // The generic (all-member) manuals. They are the flight school's
      // uploads, but for everyone, so they live here rather than in the
      // Flight school area, which a member outside the school never sees.
      { to: '/club/manuals', labelKey: 'nav.clubManuals', icon: 'mdi:bookshelf', permissions: [PERMISSIONS.fsmRead] },
    ],
  },
]

// Pre-area URLs, kept working for bookmarks and links already sent around.
export const LEGACY_REDIRECTS: Record<string, string> = {
  '/calendar': '/bookings/calendar',
  '/my-bookings': '/bookings/mine',
  '/book': '/bookings/new',
  '/maintenance': '/fleet/maintenance',
  '/notams': '/fleet/messages',
  // Step 2 of the fsm merge briefly shipped this before the rename.
  '/fleet/notams': '/fleet/messages',
  '/members': '/club/members',
}

type Can = (...permissions: string[]) => boolean

export function visibleAreas(can: Can): Area[] {
  return AREAS.map((area) => ({
    ...area,
    items: area.items.filter((item) => !item.permissions || can(...item.permissions)),
  })).filter((area) => area.items.length > 0)
}

export function areaForPath(areas: Area[], pathname: string): Area | undefined {
  return areas.find((area) => pathname === area.basePath || pathname.startsWith(`${area.basePath}/`))
}

// The item a path belongs to: the longest `to` that is the path or a
// prefix of it, so /fleet/OH-CAM highlights Aircraft while
// /fleet/maintenance highlights Maintenance.
export function activeItem(area: Area | undefined, pathname: string): AreaItem | undefined {
  let best: AreaItem | undefined
  for (const item of area?.items ?? []) {
    const matches = pathname === item.to || pathname.startsWith(`${item.to}/`)
    if (matches && (!best || item.to.length > best.to.length)) best = item
  }
  return best
}
